import { useState } from "react";
import { Plus, Trash2, CalendarClock, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ORTHO_PHASES, PHASE_LABEL, ARCH_OPTIONS, fmtDate, todayIso } from "@/lib/ortho";

const EMPTY = { date: todayIso(), upper_arch: "", lower_arch: "", ligatures_color: "", elastics: "", procedures: "", observations: "", next_visit: "", phase: "" };

export default function OrthoControls({ caseData, controls, onChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, upper_arch: caseData.upper_arch || "", lower_arch: caseData.lower_arch || "", elastics: caseData.elastics || "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/ortho/${caseData.id}/controls`, { ...form, next_visit: form.next_visit || null, phase: form.phase || null });
      toast.success("Control registrado");
      setOpen(false);
      setForm({ ...EMPTY, upper_arch: form.upper_arch, lower_arch: form.lower_arch, elastics: form.elastics });
      onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este control?")) return;
    try { await api.delete(`/ortho/controls/${id}`); onChange && onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar"); }
  };

  return (
    <section className="space-y-3" data-testid="ortho-controls">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-eyebrow">Seguimiento</div>
          <h3 className="font-display font-bold text-xl tracking-tight">Controles ({controls.length})</h3>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="btn-primary" data-testid="ortho-add-control-btn">
          {open ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {open ? "Cerrar" : "Nuevo control"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="card-flat p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ortho-control-form">
          <div><label className="label-eyebrow block mb-1">Fecha</label><input type="date" required className="input-flat" data-testid="ctrl-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
          <div><label className="label-eyebrow block mb-1">Fase</label>
            <select className="input-flat" value={form.phase} onChange={(e) => set("phase", e.target.value)}>
              <option value="">Sin cambio ({PHASE_LABEL[caseData.phase]})</option>
              {ORTHO_PHASES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select></div>
          <div><label className="label-eyebrow block mb-1">Arco superior</label><input list="arch-options-ctrl" className="input-flat font-mono" data-testid="ctrl-upper" value={form.upper_arch} onChange={(e) => set("upper_arch", e.target.value)} /></div>
          <div><label className="label-eyebrow block mb-1">Arco inferior</label><input list="arch-options-ctrl" className="input-flat font-mono" value={form.lower_arch} onChange={(e) => set("lower_arch", e.target.value)} /></div>
          <datalist id="arch-options-ctrl">{ARCH_OPTIONS.map((a) => <option key={a} value={a} />)}</datalist>
          <div><label className="label-eyebrow block mb-1">Ligaduras / color</label><input className="input-flat" value={form.ligatures_color} onChange={(e) => set("ligatures_color", e.target.value)} placeholder="Azul, metálicas…" /></div>
          <div><label className="label-eyebrow block mb-1">Elásticos</label><input className="input-flat" value={form.elastics} onChange={(e) => set("elastics", e.target.value)} placeholder="Clase II 3/16 derecho…" /></div>
          <div className="col-span-2"><label className="label-eyebrow block mb-1">Procedimientos / activaciones</label><input className="input-flat" data-testid="ctrl-procedures" value={form.procedures} onChange={(e) => set("procedures", e.target.value)} placeholder="Cambio de arco, recementado bracket 23, dobleces…" /></div>
          <div className="col-span-2 md:col-span-3"><label className="label-eyebrow block mb-1">Observaciones</label><textarea rows={2} className="input-flat h-auto py-2" value={form.observations} onChange={(e) => set("observations", e.target.value)} /></div>
          <div><label className="label-eyebrow block mb-1">Próximo control</label><input type="date" className="input-flat" data-testid="ctrl-next" value={form.next_visit} onChange={(e) => set("next_visit", e.target.value)} /></div>
          <div className="col-span-2 md:col-span-4 flex justify-end">
            <button type="submit" disabled={loading} className="btn-primary" data-testid="ctrl-save">{loading ? "Guardando…" : "Guardar control"}</button>
          </div>
        </form>
      )}

      {controls.length === 0 ? (
        <div className="card-flat p-8 text-center text-sm text-muted-foreground">Aún no hay controles registrados.</div>
      ) : (
        <ol className="relative border-l-2 border-border ml-3 space-y-4">
          {controls.map((c, i) => (
            <li key={c.id} className="ml-5 relative" data-testid={`ortho-control-${c.id}`}>
              <span className={`absolute -left-[27px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-card ${i === 0 ? "bg-primary" : "bg-border"}`} />
              <div className="card-flat p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{fmtDate(c.date)} · Control #{controls.length - i}</div>
                    <div className="text-sm font-semibold mt-0.5">{c.procedures || "Control de rutina"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.phase && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/20">{PHASE_LABEL[c.phase]}</span>}
                    {(user?.role === "admin" || user?.role === "odontologo") && (
                      <button onClick={() => remove(c.id)} className="p-1.5 text-muted-foreground hover:text-destructive" aria-label="Eliminar control"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                  <div><div className="label-eyebrow !text-[9px]">Arco sup.</div><div className="font-mono">{c.upper_arch || "—"}</div></div>
                  <div><div className="label-eyebrow !text-[9px]">Arco inf.</div><div className="font-mono">{c.lower_arch || "—"}</div></div>
                  <div><div className="label-eyebrow !text-[9px]">Ligaduras</div><div>{c.ligatures_color || "—"}</div></div>
                  <div><div className="label-eyebrow !text-[9px]">Elásticos</div><div>{c.elastics || "—"}</div></div>
                </div>
                {c.observations && <p className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">{c.observations}</p>}
                <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                  <span>{c.doctor_name}</span>
                  {c.next_visit && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Próximo: {fmtDate(c.next_visit)}</span>}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
